/**
 * Marketplace
 * -----------
 * General Marketplace page. Composed of:
 *   1. <FeaturedDealerHero />  -- weekly rotating dealer showcase
 *   2. <FilterPanel />         -- sidebar on desktop, <details> drawer on mobile
 *   3. Product grid            -- responsive grid of <ProductCard />
 *
 * Data flow:
 *   * Categories and products load in parallel on mount.
 *   * Re-runs the products fetch whenever the user clicks "Apply filters".
 *   * Backend already sorts featured products to the top; we just render.
 */
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import type { CategoryNode, Product, ProductCondition } from "../../lib/types";
import { ProductCard } from "../../components/marketplace/ProductCard";
import {
  FilterPanel,
  type MarketplaceFilters,
} from "../../components/marketplace/FilterPanel";
import { FeaturedDealerHero } from "../../components/marketplace/FeaturedDealerHero";

function toQueryParams(f: MarketplaceFilters): Record<string, string | boolean> {
  const params: Record<string, string | boolean> = {};
  if (f.category_id) params.category_id = f.category_id;
  if (f.condition) params.condition = f.condition;
  if (f.min_price) params.min_price = f.min_price;
  if (f.max_price) params.max_price = f.max_price;
  if (f.featured_only) params.featured_only = true;
  if (f.fitment.trim()) params.fitment = f.fitment.trim();
  return params;
}

/** Phase 15 — read filter state from the URL on mount so pages with
 *  ?fitment=Bayliner / ?category_id=... etc. are shareable + survive
 *  a refresh. */
function filtersFromUrl(sp: URLSearchParams): MarketplaceFilters {
  return {
    category_id: sp.get("category_id") ?? "",
    condition: (sp.get("condition") ?? "") as ProductCondition | "",
    min_price: sp.get("min_price") ?? "",
    max_price: sp.get("max_price") ?? "",
    featured_only: sp.get("featured_only") === "true",
    fitment: sp.get("fitment") ?? "",
  };
}

/** Inverse of `filtersFromUrl`. Empty values are omitted to keep the
 *  URL short. */
function urlFromFilters(f: MarketplaceFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.category_id) sp.set("category_id", f.category_id);
  if (f.condition) sp.set("condition", f.condition);
  if (f.min_price) sp.set("min_price", f.min_price);
  if (f.max_price) sp.set("max_price", f.max_price);
  if (f.featured_only) sp.set("featured_only", "true");
  if (f.fitment.trim()) sp.set("fitment", f.fitment.trim());
  return sp;
}

export default function Marketplace() {
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = React.useState<MarketplaceFilters>(() =>
    filtersFromUrl(sp),
  );
  const [categories, setCategories] = React.useState<CategoryNode[]>([]);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);

  const fetchProducts = React.useCallback(
    async (f: MarketplaceFilters) => {
      setLoading(true);
      try {
        const r = await api.get<Product[]>("/products", {
          params: toQueryParams(f),
        });
        setProducts(r.data);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /** Phase 15 — Apply mutates the URL so the filter set is shareable. */
  const applyFilters = React.useCallback(() => {
    setSp(urlFromFilters(filters), { replace: true });
    fetchProducts(filters);
  }, [filters, fetchProducts, setSp]);

  React.useEffect(() => {
    (async () => {
      const cats = await api.get<CategoryNode[]>("/categories?tree=true");
      setCategories(cats.data);
      // Initial fetch uses whatever the URL said (could be empty).
      await fetchProducts(filtersFromUrl(sp));
    })();
    // We intentionally read `sp` only once on mount — subsequent URL
    // changes flow from the user clicking Apply, which we already
    // re-render through `filters`. Don't add `sp` to the dep list
    // unless you've thought through the infinite-loop case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProducts]);

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 md:py-12">
      <FeaturedDealerHero />

      <header className="mb-6 md:mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">
            Marketplace
          </h1>
          <p className="text-ink-muted text-sm md:text-base">
            {loading ? "Loading listings…" : `${products.length} parts for sale`}
          </p>
        </div>
      </header>

      {/* Mobile filter drawer */}
      <details
        data-testid="mobile-filter-drawer"
        className="md:hidden mb-6 group"
      >
        <summary className="list-none cursor-pointer flex items-center justify-between rounded-xl border-2 border-surface-border bg-white px-4 py-3 font-semibold text-ink btn-target">
          Filters
          <span className="text-ink-muted text-sm group-open:hidden">Tap to expand</span>
          <span className="text-ink-muted text-sm hidden group-open:inline">Tap to hide</span>
        </summary>
        <div className="mt-3">
          <FilterPanel
            filters={filters}
            categories={categories}
            onChange={setFilters}
            onApply={applyFilters}
            layout="drawer"
          />
        </div>
      </details>

      <div className="grid md:grid-cols-[280px_minmax(0,1fr)] gap-8">
        <aside className="hidden md:block min-w-0">
          <FilterPanel
            filters={filters}
            categories={categories}
            onChange={setFilters}
            onApply={applyFilters}
          />
        </aside>

        <div>
          {loading && (
            <div className="py-16 text-center">
              <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
            </div>
          )}

          {!loading && products.length === 0 && (
            <div
              data-testid="marketplace-empty"
              className="rounded-2xl border border-surface-border bg-white p-10 text-center"
            >
              <p className="text-ink font-semibold mb-1">No parts match your filters.</p>
              <p className="text-ink-muted text-sm">Try relaxing the price range or condition.</p>
            </div>
          )}

          {!loading && products.length > 0 && (
            <div
              data-testid="marketplace-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
